<template>
    <Primitive
        :as="as"
        :class="classes"
        v-bind="nativeAttrs"
        @click.capture="preventDisabledActivation"
    >
        <span
            v-if="$slots['icon-start']"
            :class="element('icon', modifier('start'))"
            aria-hidden="true"
        >
            <slot name="icon-start" />
        </span>

        <span
            v-if="$slots.default || label"
            :class="element('label')"
        >
            <slot>{{ label }}</slot>
        </span>

        <span
            v-if="$slots['icon-end']"
            :class="element('icon', modifier('end'))"
            aria-hidden="true"
        >
            <slot name="icon-end" />
        </span>
    </Primitive>
</template>

<script setup lang="ts">
import type { KButtonProps } from './types'
import useBem from '@yeeroen/use-bem'
import { Primitive } from 'reka-ui'
import { computed, useAttrs, useSlots } from 'vue'

defineOptions({ name: 'KButton' })

const props = withDefaults(defineProps<KButtonProps>(), {
    as: 'button',
    variant: 'primary',
    size: 'md',
    fullWidth: false,
    disabled: false,
})

defineSlots<{
    'default'?: () => unknown
    'icon-start'?: () => unknown
    'icon-end'?: () => unknown
}>()

const slots = useSlots()
const attrs = useAttrs()

const { block, element, modifier } = useBem('k-button')

const isNativeButton = computed(() => props.as === 'button')
const isDisabled = computed(() => props.disabled)

const classes = computed(() => block(
    modifier(props.variant),
    modifier(props.size),
    modifier('full-width', props.fullWidth),
    modifier('disabled', isDisabled.value),
    modifier('icon-only', !slots.default && !props.label),
))

const nativeAttrs = computed(() => (isNativeButton.value
    ? { type: (attrs.type ?? 'button') as string, disabled: isDisabled.value || undefined }
    : { 'aria-disabled': isDisabled.value || undefined }))

function preventDisabledActivation(event: MouseEvent) {
    if (!isNativeButton.value && isDisabled.value) {
        event.preventDefault()
        event.stopImmediatePropagation()
    }
}
</script>

<style lang="scss">
@use './k-button.scss';
</style>
